/**
 * AssessmentPlayer Component
 * Main component for taking assessments
 * Features: timer, progress tracking, question navigation, auto-save
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock,
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Send,
  X,
} from 'lucide-react';
import {
  AssessmentLearner,
  AssessmentSubmission,
  AssessmentAttempt,
  QuestionLearner,
  AttemptAnswer,
  AttemptSubmitResponse,
} from '@/types/assessment.types';
import {
  SingleChoiceQuestion,
  MultipleChoiceQuestion,
  TrueFalseQuestion,
  TextAnswerQuestion,
} from './questions';
import {
  saveAssessmentAttempt,
  removeAssessmentAttempt,
} from '@/utils/assessmentStorage';
import { assessmentService } from '@/services/assessment.service';

export interface AssessmentPlayerProps {
  assessment: AssessmentLearner;
  onSubmit: (result: AttemptSubmitResponse) => void;
  onExit: (submission?: AttemptSubmitResponse) => void;
  savedAttempt?: AssessmentAttempt;
  attemptId?: string; // Optional: if already started from parent
  // Read-only preview (admin course preview): questions are shown but cannot be
  // answered or submitted, and no backend attempt is started.
  readOnly?: boolean;
  breadcrumb?: {
    moduleNumber: string | number;
    moduleTitle: string;
    lessonNumber: string | number;
    lessonTitle: string;
    onModuleClick?: () => void;
    onLessonClick?: () => void;
  };
}

export const AssessmentPlayer: React.FC<AssessmentPlayerProps> = ({
  assessment,
  onSubmit,
  onExit,
  savedAttempt,
  attemptId: propAttemptId,
  readOnly = false,
  breadcrumb,
}) => {
  // State
  const [attemptId, setAttemptId] = useState<string | null>(propAttemptId || null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>(savedAttempt?.answers || {});
  const [timeRemaining, setTimeRemaining] = useState<number | null>(
    savedAttempt?.time_remaining ?? (assessment.time_limit_minutes ? assessment.time_limit_minutes * 60 : null)
  );
  const [startTime] = useState<number>(Date.now());
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const currentQuestion = assessment.questions[currentQuestionIndex];
  const totalQuestions = assessment.questions.length;
  const answeredCount = Object.keys(answers).filter(key => {
    const answer = answers[key];
    if (Array.isArray(answer)) return answer.length > 0;
    if (typeof answer === 'string') return answer.trim().length > 0;
    return answer !== undefined && answer !== null;
  }).length;

  // Start attempt when component mounts (only if not already provided).
  // In read-only preview we never start a backend attempt.
  useEffect(() => {
    if (readOnly || attemptId) return; // Skip in preview or if already provided

    const initializeAttempt = async () => {
      try {
        const attemptResponse = await assessmentService.startAttempt(assessment.id);
        setAttemptId(attemptResponse.id);
      } catch (error: any) {
        console.error('Failed to start assessment attempt:', error);
        setSubmitError(error.message || 'Failed to start assessment. Please try again.');
      }
    };

    initializeAttempt();
  }, [assessment.id, attemptId]);

  // Timer countdown (disabled in read-only preview).
  useEffect(() => {
    if (readOnly || timeRemaining === null || timeRemaining <= 0) return;

    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev === null || prev <= 1) {
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timeRemaining]);

  // Auto-save to localStorage every 30 seconds (not in read-only preview)
  useEffect(() => {
    if (readOnly) return;
    const interval = setInterval(() => {
      saveProgress();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [answers, timeRemaining]);

  // Save progress to localStorage
  const saveProgress = useCallback(() => {
    try {
      const attempt: AssessmentAttempt = {
        assessment_id: assessment.id,
        started_at: savedAttempt?.started_at || new Date().toISOString(),
        time_remaining: timeRemaining ?? undefined,
        answers,
        question_order: assessment.questions.map(q => q.id),
      };
      saveAssessmentAttempt(attempt);
    } catch (error) {
      console.error('Failed to save assessment progress:', error);
    }
  }, [assessment.id, assessment.questions, answers, timeRemaining, savedAttempt]);

  // Handle answer change
  const handleAnswerChange = (questionId: string, answer: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  // Navigation
  const goToQuestion = (index: number) => {
    if (index >= 0 && index < totalQuestions) {
      setCurrentQuestionIndex(index);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  // Format answers for API submission
  const formatAnswersForSubmission = (): AttemptAnswer[] => {
    return assessment.questions.map(question => {
      const userAnswer = answers[question.id];
      const attemptAnswer: AttemptAnswer = {
        question_id: question.id,
      };

      switch (question.question_type) {
        case 'SINGLE_CHOICE':
          attemptAnswer.selected_option_ids = userAnswer ? [userAnswer] : [];
          break;
        case 'MULTIPLE_CHOICE':
          attemptAnswer.selected_option_ids = userAnswer || [];
          break;
        case 'TRUE_FALSE':
          if (userAnswer !== undefined) {
            if (typeof userAnswer === 'string') {
              // Options were present — userAnswer is already an option ID
              attemptAnswer.selected_option_ids = [userAnswer];
            } else {
              // No options — userAnswer is boolean, send as string
              attemptAnswer.selected_option_ids = [userAnswer.toString()];
            }
          } else {
            attemptAnswer.selected_option_ids = [];
          }
          break;
        case 'TEXT_ANSWER':
          attemptAnswer.text_answer = userAnswer || '';
          break;
      }

      return attemptAnswer;
    });
  };

  // Submit
  const handleAutoSubmit = async () => {
    if (!attemptId) {
      setSubmitError('No active attempt. Please refresh and try again.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const formattedAnswers = formatAnswersForSubmission();
      const result = await assessmentService.submitAttempt(attemptId, formattedAnswers);
      removeAssessmentAttempt(assessment.id);
      onSubmit(result);
    } catch (error: any) {
      console.error('Failed to submit assessment:', error);
      setSubmitError(error.message || 'Failed to submit assessment. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleSubmit = () => {
    setShowSubmitConfirm(false);
    handleAutoSubmit();
  };

  const handleExit = () => {
    // Just exit without submitting - user can resume or retake later
    onExit();
  };

  // Format time
  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Render question based on type
  const renderQuestion = (question: QuestionLearner) => {
    const questionAnswer = answers[question.id];

    switch (question.question_type) {
      case 'SINGLE_CHOICE':
        return (
          <SingleChoiceQuestion
            question={question}
            selectedAnswer={questionAnswer}
            onSelect={answer => handleAnswerChange(question.id, answer)}
          />
        );

      case 'MULTIPLE_CHOICE':
        return (
          <MultipleChoiceQuestion
            question={question}
            selectedAnswers={questionAnswer || []}
            onSelect={answers => handleAnswerChange(question.id, answers)}
          />
        );

      case 'TRUE_FALSE':
        return (
          <TrueFalseQuestion
            question={question}
            selectedAnswer={questionAnswer}
            onSelect={answer => handleAnswerChange(question.id, answer)}
          />
        );

      case 'TEXT_ANSWER':
        return (
          <TextAnswerQuestion
            question={question}
            answer={questionAnswer || ''}
            onChange={text => handleAnswerChange(question.id, text)}
          />
        );

      default:
        return <div className="text-red-600">Unknown question type</div>;
    }
  };

  return (
    <div className="lg:h-full bg-gray-50 flex flex-col">
      {/* Error Banner */}
      {submitError && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-red-800">
            <AlertCircle className="w-4 h-4" />
            <span>{submitError}</span>
          </div>
          <button
            onClick={() => setSubmitError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header - Unified Style. Two parts: below lg the page scrolls as one, so
          the breadcrumb scrolls away while the progress / timer / Exit row
          stays pinned at the top. At lg the header sits above the scrolling
          questions anyway, so both parts always show. */}
      {breadcrumb && (
        <div className="bg-white px-4 sm:px-6 pt-3 sm:pt-4">
          {/* Breadcrumb — on phones the short crumbs stay on one line and the
              assessment title takes its own line below them, instead of every
              item being squeezed into a narrow column. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-gray-600 min-w-0">
            <button
              onClick={breadcrumb.onModuleClick}
              className="font-medium hover:text-gray-900 transition-colors whitespace-nowrap"
              title={breadcrumb.moduleTitle}
            >
              Module {breadcrumb.moduleNumber}
            </button>
            <span className="text-gray-400">›</span>
            <button
              onClick={breadcrumb.onLessonClick}
              className="font-medium hover:text-gray-900 transition-colors whitespace-nowrap"
              title={breadcrumb.lessonTitle}
            >
              Lesson {breadcrumb.lessonNumber}
            </button>
            <span className="text-gray-400">›</span>
            <span className="font-medium whitespace-nowrap">Assessment</span>
            <span className="text-gray-400 hidden sm:inline">›</span>
            <span className="text-gray-900 font-semibold text-sm basis-full sm:basis-auto min-w-0 break-words">{assessment.title}</span>
          </div>
        </div>
      )}

      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className={`px-4 sm:px-6 pb-3 sm:pb-4 ${breadcrumb ? 'pt-2' : 'pt-3 sm:pt-4'}`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 lg:gap-3">
            {/* Left: Question Progress */}
            <div className="min-w-0">
              {!breadcrumb && <h1 className="text-lg font-semibold text-gray-900 mb-1 truncate">{assessment.title}</h1>}
              <p className="text-xs text-gray-500">
                Question {currentQuestionIndex + 1} of {totalQuestions}
              </p>
            </div>

            {/* Right: Timer, Answered Count, Exit Button */}
            <div className="flex items-center flex-wrap gap-2 lg:gap-3 flex-shrink-0">
              {!readOnly && timeRemaining !== null && (
                <div
                  className={`flex items-center gap-1.5 text-xs whitespace-nowrap ${
                    timeRemaining < 300
                      ? 'text-red-600'
                      : timeRemaining < 600
                      ? 'text-amber-600'
                      : 'text-gray-500'
                  }`}
                >
                  <Clock className="w-4 h-4 flex-shrink-0" />
                  <span className="font-mono font-semibold">
                    {formatTime(timeRemaining)}
                  </span>
                </div>
              )}
              {!readOnly && (
                <div className="text-xs text-gray-500 whitespace-nowrap">
                  {answeredCount} / {totalQuestions} answered
                </div>
              )}
              <button
                onClick={() => setShowExitConfirm(true)}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Exit Assessment
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:flex-1 lg:overflow-y-auto p-4 sm:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Question Area */}
          <div className="lg:col-span-3">
            {readOnly && (
              <div className="mb-4 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Preview Mode</p>
                  <p className="text-xs text-blue-700">You're viewing the questions in preview — answering and submitting are disabled.</p>
                </div>
              </div>
            )}
            <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 ${readOnly ? 'pointer-events-none select-none' : ''}`}>
              {renderQuestion(currentQuestion)}
            </div>

            {/* Navigation — between questions. Phones & tablets name them
                "… Question" and make them equal width, so they read apart from
                the lesson Previous / Next bar pinned below the page. */}
            <div className="mt-4 flex items-center justify-between gap-3">
              <button
                onClick={goToPreviousQuestion}
                disabled={currentQuestionIndex === 0}
                className="flex-1 lg:flex-none justify-center flex items-center gap-1 lg:gap-2 px-2 lg:px-4 py-2 min-h-[44px] lg:min-h-0 text-[13px] lg:text-sm leading-tight font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous<span className="lg:hidden"> Question</span>
              </button>

              {currentQuestionIndex === totalQuestions - 1 ? (
                readOnly ? (
                  <button
                    type="button"
                    disabled
                    title="Submitting is disabled in preview"
                    className="flex-1 lg:flex-none justify-center flex items-center gap-1 lg:gap-2 px-2 lg:px-6 py-2 min-h-[44px] lg:min-h-0 text-[13px] lg:text-sm leading-tight font-medium whitespace-nowrap text-white bg-primary-600 rounded-lg opacity-50 cursor-not-allowed"
                  >
                    <Send className="w-4 h-4 flex-shrink-0 max-[359px]:hidden" />
                    Submit disabled in preview
                  </button>
                ) : (
                  <button
                    onClick={() => setShowSubmitConfirm(true)}
                    disabled={isSubmitting || !attemptId}
                    className="flex-1 lg:flex-none justify-center flex items-center gap-1 lg:gap-2 px-2 lg:px-6 py-2 min-h-[44px] lg:min-h-0 text-[13px] lg:text-sm leading-tight font-medium whitespace-nowrap text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4 flex-shrink-0 max-[359px]:hidden" />
                    {isSubmitting ? 'Submitting...' : 'Submit Assessment'}
                  </button>
                )
              ) : (
                <button
                  onClick={goToNextQuestion}
                  className="flex-1 lg:flex-none justify-center flex items-center gap-1 lg:gap-2 px-2 lg:px-4 py-2 min-h-[44px] lg:min-h-0 text-[13px] lg:text-sm leading-tight font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Next<span className="lg:hidden"> Question</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Question Navigator */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sticky top-24 overflow-hidden">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Questions</h3>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(36px,1fr))] gap-2">
                {assessment.questions.map((q, index) => {
                  const isAnswered = answers[q.id] !== undefined && answers[q.id] !== null &&
                    (typeof answers[q.id] !== 'string' || answers[q.id].trim() !== '') &&
                    (!Array.isArray(answers[q.id]) || answers[q.id].length > 0);
                  const isCurrent = index === currentQuestionIndex;

                  return (
                    <button
                      key={q.id}
                      onClick={() => goToQuestion(index)}
                      className={`w-10 h-10 lg:w-8 lg:h-8 rounded-lg text-sm lg:text-xs font-medium transition-all flex items-center justify-center ${
                        isCurrent
                          ? 'bg-primary-600 text-white'
                          : isAnswered
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {index + 1}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-primary-600 rounded flex-shrink-0"></div>
                  <span className="text-gray-600">Current</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-green-100 rounded flex-shrink-0"></div>
                  <span className="text-gray-600">Answered</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-gray-100 rounded flex-shrink-0"></div>
                  <span className="text-gray-600">Unanswered</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Confirmation Modal */}
      {showSubmitConfirm && (
        <ConfirmModal
          title="Submit Assessment?"
          message={
            answeredCount < totalQuestions
              ? `You have answered ${answeredCount} out of ${totalQuestions} questions. Unanswered questions will be marked as incorrect. Are you sure you want to submit?`
              : `You have answered all ${totalQuestions} questions. Are you sure you want to submit your assessment?`
          }
          icon={answeredCount < totalQuestions ? <AlertCircle className="w-12 h-12 text-amber-600" /> : <CheckCircle className="w-12 h-12 text-green-600" />}
          confirmText="Submit"
          confirmStyle="primary"
          onConfirm={handleSubmit}
          onCancel={() => setShowSubmitConfirm(false)}
        />
      )}

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <ConfirmModal
          title="Exit Assessment?"
          message="Your progress won't be saved. Are you sure you want to exit?"
          icon={<AlertCircle className="w-12 h-12 text-amber-600" />}
          confirmText="Exit"
          confirmStyle="secondary"
          onConfirm={handleExit}
          onCancel={() => setShowExitConfirm(false)}
        />
      )}
    </div>
  );
};

// ============================================================================
// Confirmation Modal Component
// ============================================================================

interface ConfirmModalProps {
  title: string;
  message: string;
  icon: React.ReactNode;
  confirmText: string;
  confirmStyle: 'primary' | 'secondary';
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title,
  message,
  icon,
  confirmText,
  confirmStyle,
  onConfirm,
  onCancel,
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex flex-col items-center text-center">
          {/* Icon */}
          <div className="mb-4">{icon}</div>

          {/* Title */}
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>

          {/* Message */}
          <p className="text-sm text-gray-600 mb-6">{message}</p>

          {/* Actions */}
          <div className="flex gap-3 w-full">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg ${
                confirmStyle === 'primary'
                  ? 'bg-primary-600 hover:bg-primary-700'
                  : 'bg-gray-600 hover:bg-gray-700'
              }`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
